import { z } from "zod";
import { taxPolicyVersionSchema } from "./rule-drafts.js";
/*
 * Governed auto-approval (ADR 0020). Every stage ships disabled. Enabling a
 * stage is a platform administrator's audited action; the pipeline itself can
 * never enable a stage. The preview is read-only and always available.
 */
/** Version of the auto-approval criteria. Any change to a criterion bumps it. */
export const AUTO_APPROVAL_POLICY_VERSION = "v2";
/** Audit actor subject of every action the pipeline takes. */
export const AUTO_APPROVAL_ACTOR_SUBJECT = `system:auto-approval@${AUTO_APPROVAL_POLICY_VERSION}`;
export const autoApprovalStageSchema = z.enum([
    "EVIDENCE",
    /** Stage 0 "importar pacote": creates PENDING_REVIEW proposals from a shipped rule package. */
    "PACKAGE",
    "FACTS",
    "PROPOSALS",
    "POLICY_SWITCH",
    "PUBLISH",
]);
/** Execution order of a run. */
export const AUTO_APPROVAL_STAGE_ORDER = [
    "EVIDENCE",
    // Runs after evidence so a package item can be created once its evidence is approved.
    "PACKAGE",
    "FACTS",
    "PROPOSALS",
    "POLICY_SWITCH",
    "PUBLISH",
];
/** Official act kinds recognised by `classifyOfficialAct` (stage 1). */
export const officialActKindSchema = z.enum([
    "LEI",
    "LEI_COMPLEMENTAR",
    "DECRETO",
    "RICMS",
    "PORTARIA",
    "RESOLUCAO",
    "INSTRUCAO_NORMATIVA",
    "CONVENIO_ICMS",
    "PROTOCOLO_ICMS",
    "AJUSTE_SINIEF",
    "ATO_COTEPE",
    "DECISAO_NORMATIVA",
    "COMUNICADO",
    "TIPI",
    "UNKNOWN",
]);
/**
 * Criterion codes. A passed criterion is listed in `passed`, a failed one in
 * `reasons` (the hold reasons). Codes are stable API values; UI copy is keyed
 * by them.
 */
export const autoApprovalCriterionSchema = z.enum([
    // Shared
    "STAGE_DISABLED",
    // Stage 0: package import
    "PACKAGE_EVIDENCE_READY",
    "PACKAGE_EVIDENCE_CAPTURED",
    "PACKAGE_TARGET_CURRENT",
    "PACKAGE_NOT_IMPORTED",
    "PACKAGE_DEPENDENCY_READY",
    "PACKAGE_GROUP_ENABLED",
    "KILL_SWITCH",
    "EVALUATION_FAILED",
    "LIMIT_REACHED",
    "CHANGED_SINCE_EVALUATION",
    // Stage 1: evidence
    "SOURCE_REGISTERED",
    "URL_IN_REGISTRY",
    "SOURCE_HEALTHY",
    "FETCH_OK",
    "DIGEST_STABLE",
    "ACT_SUPPORTED",
    // Stage 2: facts
    "EVIDENCE_APPROVED",
    "INDEPENDENT_AGREEMENT",
    "NO_CERTIFIED_CONFLICT",
    "NO_DRIFT",
    /** The whole effective interval, rather than a single overlapping date, is corroborated. */
    "FULL_VALIDITY_COVERAGE",
    // Stage 3: proposals (EVIDENCE_APPROVED is shared with stage 2)
    "DRY_RUN_VALID",
    "NO_PENDING_OVERLAP",
    "WITHIN_THRESHOLDS",
    "RATE_DELTA_WITHIN_CAP",
    "MVA_DELTA_WITHIN_CAP",
    "NO_DELETION",
    "NEW_RULES_ALLOWED",
    "AFFECTED_RULES_WITHIN_CAP",
    "POLICY_UNCHANGED",
    "WAITING_FOR_DEPENDENCY",
    /** Derived from stored authority; an approved document is not per-field applicability proof. */
    "QUALIFIED_APPLICABILITY",
    // Stage 5: policy switch
    "POLICY_SWITCH_REQUIRES_HUMAN",
    "TARGET_POLICY_READY",
    // Stage 4: publish
    "DRAFT_PRESENT",
    "BASE_CURRENT",
    "READINESS_ZERO",
    "DIFF_WITHIN_BOUNDS",
    "ONLY_AUTO_APPROVED",
    "COOLING_OFF",
    "CANARY_PASSED",
    "BREAKER_TRIPPED",
    "POST_PUBLISH_CANARY_PASSED",
]);
/* -- Settings ----------------------------------------------------------------- */
const pp = z.number().min(0).max(100).multipleOf(0.01);
export const autoApprovalStagesSchema = z
    .object({
    evidence: z.boolean(),
    /** Stage 0: import the shipped rule package as PENDING_REVIEW proposals. */
    packageImport: z.boolean().default(false),
    facts: z.boolean(),
    proposals: z.boolean(),
    publish: z.boolean(),
    /** Stage 5 is never implied by another stage. */
    policySwitch: z.boolean(),
})
    .strict();
export const autoApprovalThresholdsSchema = z
    .object({
    evidence: z
        .object({
        /** Fetches that returned the same digest (receipts). */
        minObservations: z.number().int().min(1).max(20),
        /** Or: first seen at least this long ago with no newer digest. */
        minStableHours: z
            .number()
            .int()
            .min(0)
            .max(24 * 60),
        supportedActs: z.array(officialActKindSchema.exclude(["UNKNOWN"])).max(14),
        maxPerRun: z.number().int().min(1).max(500),
        /** Owner priority routes `ORIGIN-DESTINATION` (e.g. `ES-SP`) for need-based ordering. */
        priorityRoutes: z
            .array(z.string().regex(/^[A-Z]{2}-[A-Z]{2}$/))
            .max(27 * 27)
            .default([]),
        /**
         * True: also require the reviewed path prefix. Default false: a registered
         * official host plus a successful fetch is the trust boundary (same as ingest).
         */
        requirePathPrefix: z.boolean().default(false),
    })
        .strict(),
    package: z
        .object({
        /** Shipped package id, e.g. `2026-10-05`. */
        packageId: z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}(\.[0-9]+)?$/),
        /** Owner-optional groups (8 and 9 of the 2026-10-05 package). */
        includeOptionalGroups: z.boolean(),
        maxPerRun: z.number().int().min(1).max(500),
    })
        .strict()
        .default({ packageId: "2026-10-05", includeOptionalGroups: false, maxPerRun: 100 }),
    facts: z
        .object({
        maxPerRun: z.number().int().min(1).max(1000),
    })
        .strict(),
    proposals: z
        .object({
        maxRateDeltaPp: pp,
        maxMvaDeltaPp: pp,
        allowDeletions: z.boolean(),
        allowNewRules: z.boolean(),
        /** Rules added or changed by the approvals of one run. */
        maxAffectedRules: z.number().int().min(1).max(1000),
        maxPerRun: z.number().int().min(1).max(500),
    })
        .strict(),
    publish: z
        .object({
        maxAddedRules: z.number().int().min(0).max(5000),
        maxChangedRules: z.number().int().min(0).max(5000),
        maxRemovedRules: z.number().int().min(0).max(5000),
        /** False: hold when the draft carries proposals a person approved. */
        includeHumanApproved: z.boolean(),
        minDraftAgeMinutes: z
            .number()
            .int()
            .min(0)
            .max(7 * 24 * 60),
    })
        .strict(),
    policy: z
        .object({
        targetPolicyVersion: taxPolicyVersionSchema,
    })
        .strict(),
})
    .strict();
export const autoApprovalSettingsUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    stages: autoApprovalStagesSchema,
    thresholds: autoApprovalThresholdsSchema,
    /** Required on every change; audited. */
    reason: z.string().trim().min(10).max(500),
})
    .strict();
export const autoApprovalBreakerSchema = z
    .object({
    stage: autoApprovalStageSchema,
    trippedAt: z.string().datetime(),
    runId: z.string().uuid().nullable(),
    reason: z.string(),
})
    .strict();
export const autoApprovalSettingsSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    stages: autoApprovalStagesSchema,
    thresholds: autoApprovalThresholdsSchema,
    policyVersion: z.literal(AUTO_APPROVAL_POLICY_VERSION),
    actorSubject: z.literal(AUTO_APPROVAL_ACTOR_SUBJECT),
    /** Deploy-time env AUTO_APPROVAL_KILL_SWITCH (read only). */
    killSwitch: z.boolean(),
    /** Stage toggles after the kill switch and open breakers. */
    effectiveStages: autoApprovalStagesSchema,
    breakers: z.array(autoApprovalBreakerSchema),
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().uuid().nullable(),
    updatedByName: z.string().nullable(),
    lastReason: z.string().nullable(),
    revision: z.number().int().nonnegative(),
})
    .strict();
export const AUTO_APPROVAL_DEFAULT_STAGES = {
    evidence: false,
    packageImport: false,
    facts: false,
    proposals: false,
    publish: false,
    policySwitch: false,
};
export const AUTO_APPROVAL_DEFAULT_THRESHOLDS = {
    evidence: {
        minObservations: 2,
        minStableHours: 24,
        supportedActs: [
            "LEI",
            "LEI_COMPLEMENTAR",
            "DECRETO",
            "RICMS",
            "PORTARIA",
            "RESOLUCAO",
            "INSTRUCAO_NORMATIVA",
            "CONVENIO_ICMS",
            "PROTOCOLO_ICMS",
            "AJUSTE_SINIEF",
            "ATO_COTEPE",
            "DECISAO_NORMATIVA",
            "COMUNICADO",
            "TIPI",
        ],
        maxPerRun: 50,
        priorityRoutes: ["ES-SP", "PR-SP", "SP-SP", "SP-RJ", "BA-SP"],
        requirePathPrefix: false,
    },
    package: { packageId: "2026-10-05", includeOptionalGroups: false, maxPerRun: 100 },
    facts: { maxPerRun: 200 },
    proposals: {
        maxRateDeltaPp: 1,
        maxMvaDeltaPp: 2,
        allowDeletions: false,
        allowNewRules: true,
        maxAffectedRules: 25,
        maxPerRun: 50,
    },
    publish: {
        maxAddedRules: 100,
        maxChangedRules: 50,
        maxRemovedRules: 0,
        includeHumanApproved: false,
        minDraftAgeMinutes: 60,
    },
    policy: { targetPolicyVersion: "tax-family-v2" },
};
/* -- Decisions ---------------------------------------------------------------- */
export const autoApprovalResourceTypeSchema = z.enum([
    "evidence",
    "fiscal-fact",
    "rule-proposal",
    "ruleset",
]);
export const autoApprovalOutcomeSchema = z.enum([
    /** Stage 0: a PENDING_REVIEW proposal was (would be) created. */
    "CREATE",
    "APPROVE",
    "CERTIFY",
    "SWITCH_POLICY",
    "PUBLISH",
    "ROLLBACK",
    "HOLD",
]);
export const autoApprovalDecisionSchema = z
    .object({
    stage: autoApprovalStageSchema,
    resourceType: autoApprovalResourceTypeSchema,
    resourceId: z.string().min(1).max(200),
    /** Human label: document title, `NCM origin→destination`, ruleset version. */
    label: z.string().max(500),
    outcome: autoApprovalOutcomeSchema,
    passed: z.array(autoApprovalCriterionSchema),
    reasons: z.array(autoApprovalCriterionSchema),
    /** Criterion evidence, e.g. `{ rateDeltaPp: "0.50", observations: 3 }`. */
    details: z.record(z.string(), z.unknown()),
})
    .strict();
/* -- Canary ------------------------------------------------------------------- */
export const autoApprovalCanaryFailureSchema = z
    .object({
    suite: z.string(),
    caseId: z.string(),
    field: z.string().nullable(),
    expected: z.string().nullable(),
    actual: z.string().nullable(),
    kind: z.enum(["GOLDEN_MISMATCH", "UNEXPECTED_CHANGE", "STATUS_DOWNGRADE", "REPLAY_MISMATCH"]),
})
    .strict();
export const autoApprovalCanaryResultSchema = z
    .object({
    passed: z.boolean(),
    engineVersion: z.string(),
    golden: z
        .object({
        suites: z.array(z
            .object({ suite: z.string(), total: z.number().int(), passed: z.number().int() })
            .strict()),
        total: z.number().int().nonnegative(),
        passed: z.number().int().nonnegative(),
    })
        .strict(),
    replay: z
        .object({
        items: z.number().int().nonnegative(),
        unchanged: z.number().int().nonnegative(),
        expectedChanges: z.number().int().nonnegative(),
        unexpectedRegressions: z.number().int().nonnegative(),
    })
        .strict(),
    /**
     * Owner expectations evaluated against the CANDIDATE ruleset (production rules),
     * e.g. the Jamerson row (1806.31.10 ES→SP, ICMS-ST 54.66 once the art. 39 rule is in).
     * NOT_APPLICABLE when the candidate lacks the rule the expectation depends on.
     */
    expectations: z
        .array(z
        .object({
        id: z.string(),
        label: z.string(),
        field: z.string(),
        expected: z.string(),
        actual: z.string().nullable(),
        status: z.enum(["PASS", "FAIL", "NOT_APPLICABLE"]),
        note: z.string().nullable(),
    })
        .strict())
        .default([]),
    /** First 50 failures. */
    failures: z.array(autoApprovalCanaryFailureSchema).max(50),
})
    .strict();
/* -- Preview (read-only) ------------------------------------------------------ */
export const autoApprovalStagePreviewSchema = z
    .object({
    stage: autoApprovalStageSchema,
    /** The persisted toggle. The preview evaluates every stage regardless. */
    enabled: z.boolean(),
    effective: z.boolean(),
    candidates: z.number().int().nonnegative(),
    evaluated: z.number().int().nonnegative(),
    wouldAct: z.number().int().nonnegative(),
    held: z.number().int().nonnegative(),
    /** True when more candidates exist than the per-run cap evaluates. */
    truncated: z.boolean(),
    holdsByReason: z.record(z.string(), z.number().int().nonnegative()),
    /**
     * Breakdown over ALL candidates (not only the evaluated cap), e.g. failing-criterion
     * counts and distributions of source status, content type, act kind and priority tier.
     */
    diagnostics: z.record(z.string(), z.unknown()).default({}),
    /** Would-act items first, then holds; at most 200. */
    items: z.array(autoApprovalDecisionSchema).max(200),
    error: z.string().nullable(),
})
    .strict();
export const autoApprovalPreviewSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    generatedAt: z.string().datetime(),
    policyVersion: z.literal(AUTO_APPROVAL_POLICY_VERSION),
    killSwitch: z.boolean(),
    settingsRevision: z.number().int().nonnegative(),
    /**
     * `policySwitch: true` (GET /v1/auto-approval/preview?scenario=all) also assumes the
     * stage-5 policy switch would run, so the preview shows the full end-to-end path.
     */
    assumptions: z.object({ policySwitch: z.boolean() }).strict().default({ policySwitch: false }),
    /** Stages evaluated in run order; later stages assume earlier would-acts. */
    stages: z.array(autoApprovalStagePreviewSchema),
    canary: autoApprovalCanaryResultSchema.nullable(),
})
    .strict();
/* -- Runs and report ---------------------------------------------------------- */
export const autoApprovalRunTriggerSchema = z.enum(["SCHEDULE", "MANUAL"]);
export const autoApprovalRunStatusSchema = z.enum([
    /** Kill switch on or every stage off: nothing is written, not even a run row. */
    "DISABLED",
    /** Another run holds the organization lock. */
    "LOCKED",
    "RUNNING",
    "COMPLETED",
    /** At least one stage failed; other stages still ran. */
    "PARTIAL",
    "FAILED",
]);
export const autoApprovalRunSchema = z
    .object({
    id: z.string().uuid(),
    trigger: autoApprovalRunTriggerSchema,
    status: autoApprovalRunStatusSchema,
    policyVersion: z.string(),
    settingsRevision: z.number().int().nonnegative(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    triggeredBy: z.string().nullable(),
    actions: z.number().int().nonnegative(),
    holds: z.number().int().nonnegative(),
    stages: z.array(z
        .object({
        stage: autoApprovalStageSchema,
        status: z.enum(["SKIPPED", "COMPLETED", "FAILED"]),
        acted: z.number().int().nonnegative(),
        held: z.number().int().nonnegative(),
        error: z.string().nullable(),
    })
        .strict()),
})
    .strict();
export const autoApprovalActionSchema = z
    .object({
    id: z.string().uuid(),
    runId: z.string().uuid(),
    stage: autoApprovalStageSchema,
    outcome: autoApprovalOutcomeSchema.exclude(["HOLD"]),
    resourceType: autoApprovalResourceTypeSchema,
    resourceId: z.string(),
    label: z.string(),
    passed: z.array(autoApprovalCriterionSchema),
    details: z.record(z.string(), z.unknown()),
    createdAt: z.string().datetime(),
})
    .strict();
export const autoApprovalReportSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    timezone: z.literal("America/Sao_Paulo"),
    runs: z.number().int().nonnegative(),
    counts: z
        .object({
        approvedEvidence: z.number().int().nonnegative(),
        certifiedFacts: z.number().int().nonnegative(),
        approvedProposals: z.number().int().nonnegative(),
        policySwitches: z.number().int().nonnegative(),
        published: z.number().int().nonnegative(),
        rollbacks: z.number().int().nonnegative(),
    })
        .strict(),
    actions: z.array(autoApprovalActionSchema).max(1000),
    /** Current holds (latest reasons per resource), grouped. */
    holds: z.array(z
        .object({
        stage: autoApprovalStageSchema,
        reason: autoApprovalCriterionSchema,
        count: z.number().int().nonnegative(),
    })
        .strict()),
    breakers: z.array(autoApprovalBreakerSchema),
})
    .strict();
/* -- Package status (stage 0, read-only) -------------------------------------- */
export const autoApprovalPackageStatusSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    packageId: z.string(),
    title: z.string(),
    humanPrerequisites: z.array(z.string()),
    groups: z.array(z
        .object({
        id: z.string(),
        title: z.string(),
        optional: z.boolean(),
        total: z.number().int().nonnegative(),
        imported: z.number().int().nonnegative(),
        items: z
            .array(z
            .object({
            key: z.string(),
            imported: z.boolean(),
            proposalIds: z.array(z.string().uuid()),
        })
            .strict())
            .max(500),
    })
        .strict()),
    plans: z.array(z
        .object({
        id: z.string(),
        imported: z.boolean(),
        proposalIds: z.array(z.string().uuid()),
    })
        .strict()),
})
    .strict();
//# sourceMappingURL=auto-approval.js.map